import { useFrame } from "@react-three/fiber";
import { useRef } from "react";
import { Euler, MathUtils, Object3D } from "three";
import * as ZapparThree from "@zappar/zappar-threejs";

interface IUseFaceTriggersInputs {
    enabled: boolean;
    faceTrackerGroupRef: React.RefObject<ZapparThree.FaceAnchorGroup | null>,
    onMouthOpenChange?: (open: boolean) => unknown;
    mouthOpenThreshold?: number;
    onHeadTilt?: (side: 'left' | 'right') => unknown;
    headTiltThreshold?: number;
    onHeadShake?: () => unknown;
    headDirectionChangeCountThreshold?: number;
    directionChangeMaxTimeBetweenThreshold?: number; // ms
	headShakeMaxTimeBetweenThreshold?: number; //ms
	headShakeRotationThreshold?: number; // degrees
	lipTopLandmark: Object3D | undefined,
	lipBottomLandMark: Object3D | undefined,
}

export const useFaceTriggers = ({
    faceTrackerGroupRef, 
    enabled, 
    onMouthOpenChange, 
    mouthOpenThreshold = 0.15,
    onHeadTilt,
    headTiltThreshold = 8,
    onHeadShake,
    headShakeRotationThreshold = 2,
    headDirectionChangeCountThreshold = 1,
    directionChangeMaxTimeBetweenThreshold = 1000,
	headShakeMaxTimeBetweenThreshold = 400,
	lipTopLandmark,
	lipBottomLandMark,
}: IUseFaceTriggersInputs) => {
    const currentHeadRotationRef = useRef(new Euler());
    const isMouthOpenRef = useRef(false);
    const isHeadTiltedRef = useRef(false);
	const headshakeActiveRef = useRef(false);
    const headDirectionChangeCountDownRef = useRef(headDirectionChangeCountThreshold);
	const previousDirectionChangeTimeRef = useRef<number | undefined>(undefined);
	const previousDirectionRef = useRef<1 | -1>(undefined);
	const headshakePreviousTimeRef = useRef<number>(undefined);

	const directionChangeMaxTimeBetweenThresholdSec = directionChangeMaxTimeBetweenThreshold / 1000;
	const headshakeMaxTimeBetweenThresholdSec = headShakeMaxTimeBetweenThreshold / 1000;

    useFrame(({ clock }) => {
		if (!enabled || !faceTrackerGroupRef.current) return;
		
		if (onMouthOpenChange) {
			if (!lipTopLandmark || !lipBottomLandMark) return;
			const distance = lipTopLandmark.position.distanceTo(lipBottomLandMark.position);

			if (distance > mouthOpenThreshold && !isMouthOpenRef.current) {
				isMouthOpenRef.current = true;
				onMouthOpenChange(true);
			} else if (distance < mouthOpenThreshold && isMouthOpenRef.current) {
				isMouthOpenRef.current = false;
				onMouthOpenChange(false);
			}
		}

		if (!onHeadTilt && !onHeadShake) return;

		const { rotation } = faceTrackerGroupRef.current;
		currentHeadRotationRef.current.set(rotation.x, rotation.y, rotation.z);
	
		if (onHeadTilt) {
			let currentRotationZ = MathUtils.radToDeg(currentHeadRotationRef.current.z);

			let adjCurrentRotationZ = 0;
			if (currentRotationZ < 0) {
				adjCurrentRotationZ = 180 + currentRotationZ;
			} else {
				adjCurrentRotationZ = 180 - currentRotationZ;
			}
						
			if (adjCurrentRotationZ > headTiltThreshold && adjCurrentRotationZ < 90 && !isHeadTiltedRef.current) {
				isHeadTiltedRef.current = true;
				onHeadTilt(currentRotationZ > 0 ? 'left' : 'right');
			} else if (adjCurrentRotationZ < headTiltThreshold && isHeadTiltedRef.current) {
				isHeadTiltedRef.current = false;
			}
		}

		if (onHeadShake) {
			const yDeg = MathUtils.radToDeg(currentHeadRotationRef.current.y)
			const absYdeg = Math.abs(yDeg);
			
			// if head rotation movement below threshold value return;
			if (absYdeg < headShakeRotationThreshold) return;
			
			const elapsedTime = clock.elapsedTime;
			const currentDirection = Math.sign(yDeg) as 1 | -1;

			if (typeof previousDirectionRef.current === 'undefined') {
				previousDirectionRef.current = currentDirection;
				previousDirectionChangeTimeRef.current = elapsedTime;
				headDirectionChangeCountDownRef.current --;
				return;
			}
			
			// Return if direction hasn't changed
			if (previousDirectionRef.current === currentDirection) return;
			previousDirectionRef.current = currentDirection;

			// If a direction change has occured, get time delta between direction changes
			const timeDelta = elapsedTime - previousDirectionChangeTimeRef.current!;
			previousDirectionChangeTimeRef.current = elapsedTime;

			// If direction change took too long reset direction change countdown variable and enable new headshakes to be registered
			if (timeDelta > directionChangeMaxTimeBetweenThresholdSec) {
				headDirectionChangeCountDownRef.current = headDirectionChangeCountThreshold;
				headshakeActiveRef.current = false;
				return;
			}

			// As this is a qualified direction change, reduce head direction change countdown variable
			headDirectionChangeCountDownRef.current--; 

			// beyond this point a headshake has been registered
			if (headDirectionChangeCountDownRef.current > 0) return;
			headDirectionChangeCountDownRef.current = headDirectionChangeCountThreshold;
			
			// get time between headshakes
			const headshakeTimeDelta = elapsedTime - (headshakePreviousTimeRef.current ?? elapsedTime);
			headshakePreviousTimeRef.current = elapsedTime;
				
			// if the time difference to a previous headshake is above the threshold enable new headshakes
			if ((headshakeTimeDelta > headshakeMaxTimeBetweenThresholdSec) && headshakeActiveRef.current) {
				headshakeActiveRef.current = false;
			};

			// if headshake occurred disable new headshakes (they get enabled again if time threshold between shakes )
			if (headshakeActiveRef.current) return;
		
			headshakeActiveRef.current = true;
			
			onHeadShake();
		}

	})
}